<template>
    <div id="place-wrapper">
        <div v-for="(place, index) in places" v-bind:key="index">
            <firefox-a11y-place
                :place="place"
                :index="index"
                :disabled="isContinueButtonDisabled(place.id)"
                :aria-label="ariaLabelPlaceSelected(place)"
                @onClick="onPanelInput(index, place.id)"
                @previousStep="previousStep()"
            />
        </div>
        <div data-testid="place-panel" aria-hidden="true">
            <zip-code-filter
                v-if="enableZipcodeFilter"
                :zipcode="zipCodeAnswer"
                :disabled="loading"
                ref="zipcodeInput"
                @checkForZipcode="filterPlaces(); displayPlace = true"
                @hidePlaces="displayPlace = false"
                @showPlaces="displayPlace = true"
            />
            <v-expansion-panels
                v-if="displayPlace && !loadingPlace && !zipcodeError"
                focusable
                tabindex="-1"
                aria-hidden="true"
                role="tablist"
                id="place-panel"
            >
                <v-expansion-panel
                    v-for="(place, index) in places"
                    hide-default-footer
                    :disabled="loading"
                    aria-hidden="true"
                    role="tab"
                    :value="placePanel[index]"
                    class="v-expansion-panel-odd-background"
                    :class="{'active-header-background': currentPanel === index}"
                    :key="place.id"
                    @click.native="placePanel[index] ? openPanel(-1) : openPanel(index); currentPanel = index;"
                >
                    <v-expansion-panel-header tabindex="-1" aria-hidden="true">
                        <div>
                            <v-layout>
                                <v-flex xs12 md9 align-self-center class="text-center text-md-left py-3 py-md-0">
                                    <v-tooltip top>
                                        <template v-slot:activator="{ on }">
                                            <v-btn
                                                role="tabpanel"
                                                tabindex="-1"
                                                aria-hidden="true"
                                                class="px-0 d-inline-block text-wrap"
                                                text
                                                :ripple="false"
                                                @click.prevent="checkPanelClick(index)"
                                                v-on="on"
                                            >
                                                <span
                                                    class="subheading text-none font-weight-bold"
                                                    aria-hidden="true"
                                                    style="text-decoration: underline;"
                                                    :id="placeNameId(place.id)"
                                                >
                                                    {{ place['name' + upperLocale] }}
                                                    <span v-if="showKm(!place.isDistanceHidden)">
                                                        {{ formatDistance(place.distance) }}
                                                    </span>
                                                </span>
                                            </v-btn>
                                        </template>
                                        <span tabindex="-1" aria-hidden="true" :id="tooltipId(place.id)">
                                            {{ $t('appt.tooltips.clickToSeePlaceDetails') }}
                                        </span>
                                    </v-tooltip>
                                </v-flex>

                                <v-flex
                                    v-if="$vuetify.breakpoint.mdAndUp"
                                    class="text-right"
                                >
                                    <previous-button
                                        @previousStep="() => { $emit('previousStep') }"
                                    />
                                    <next-button
                                        :aria-label="ariaLabelPlaceSelected(place)"
                                        :aria-disabled="isContinueButtonDisabled(place.id)"
                                        :loading="loading"
                                        :classes="`${isContinueButtonDisabled(place.id) ? 'btn-submit-disabled' : 'button-secondary-color'}`"
                                        :data-testid="`place-continue-${index}`"
                                        @nextStep="onPanelInput(index, place.id)"
                                    />
                                </v-flex>
                            </v-layout>
                        </div>
                    </v-expansion-panel-header>
                    <v-expansion-panel-content tabindex="-1" aria-hidden="true">
                        <v-card class="grey lighten-3 rounded-0">
                            <v-card-text
                                aria-live="true"
                                atomic="true"
                                class="service-description"
                            >
                                <template v-if="placesAvailables[place.id]">
                                    <v-layout v-if="shouldShowAddress(!place.isDistanceHidden)" row wrap>
                                        <v-flex
                                            v-if="place.formattedAddress"
                                            xs12
                                            mx-6
                                        >
                                            <span class="body-2" :id="'place-formatted-address-' + place.id">
                                                {{ place.formattedAddress }}
                                            </span>
                                        </v-flex>
                                    </v-layout>
                                </template>
                                <template v-else>
                                    <v-flex class="pt-2">
                                        <v-alert type="warning" :value="true">
                                            <div v-html="place['areaNotServedMessage' + upperLocale]" />
                                        </v-alert>
                                    </v-flex>
                                </template>
                            </v-card-text>
                            <v-card-actions
                                v-if="!$vuetify.breakpoint.mdAndUp"
                            >
                                <v-layout class="row">
                                    <v-flex xs12 class="text-center">
                                        <previous-button
                                            :allow-small="true"
                                            @previousStep="() => { $emit('previousStep') }"
                                        />
                                        <next-button
                                            :loading="loading"
                                            :allow-small="true"
                                            :aria-disabled="isContinueButtonDisabled(place.id)"
                                            :class="`${isContinueButtonDisabled(place.id) ? 'btn-submit-disabled' : 'button-secondary-color'}`"
                                            @nextStep="onPanelInput(index, place.id)"
                                        />
                                    </v-flex>
                                </v-layout>
                            </v-card-actions>
                        </v-card>
                    </v-expansion-panel-content>
                </v-expansion-panel>
            </v-expansion-panels>
            <v-layout v-if="displayPlace && loadingPlace">
                <v-flex
                    xs
                    12
                    py-12
                    my-12
                    text-center>
                    <v-progress-circular
                        indeterminate
                        :size="70"
                        :width="7"
                        :aria-label="$t('accessibility.loadingGeneric')"
                        class="screen-center"
                        color="primary"
                    />
                </v-flex>
            </v-layout>
            <v-layout v-if="displayPlace && zipcodeError">
                <v-flex xs 12 text-center>
                    <v-alert type="warning" :value="true">
                        <span>{{ $t('base.errors.zipcodeError') }}</span>
                    </v-alert>
                </v-flex>
            </v-layout>
            <v-layout v-if="displayPlace && !loadingPlace && !zipcodeError && enableZipcodeFilter" row>
                <v-flex xs12 class="text-center body-1 pt-4">
                    {{ $t('base.distanceWarning') }}
                </v-flex>
            </v-layout>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { findIndex, isInteger, isNil, reduce } from 'lodash';
import ZipCodeFilter from '@/components/steppers/ZipCodeFilter';
import { isZipCodeServed } from '@/helpers/is-zip-code-served';
import FirefoxA11yPlace from '@/components/steppers/Place/FirefoxA11yPlace';
import { analyticsEventHandler } from '@/mixins/ga-event-handler';
import PreviousButton from '@/components/buttons/PreviousButton.vue';
import NextButton from '@/components/buttons/NextButton.vue';

export default {
    name: 'step-place',
    components: {
        ZipCodeFilter,
        FirefoxA11yPlace,
        PreviousButton,
        NextButton
    },
    props: {
        stepNum: { type: Number },
        currentStep: { type: Number },
        loading: { type: Boolean, required: true },
        enableZipcodeFilter: { type: Boolean, required: true },
    },
    data() {
        return {
            displayPlace: false,
            showDistance: true,
            placePanel: [],
            panelReadOnly: true,
            loadingPlace: false,
            zipcodeError: false,
            postalCodeFromPortal: false,
        };
    },
    created() {
        if (!this.enableZipcodeFilter) {
            this.displayPlace = true;
        }
        this.$store.commit('services/SET_IS_SELECTED_SERVICE', true);
    },

    mounted() {
        const selectedPlacePanel = this.getSelectedPlacePanel;
        if (isInteger(selectedPlacePanel)) {
            this.openPanel(selectedPlacePanel);
        }
    },
    mixins: [analyticsEventHandler],
    computed: {
        ...mapGetters({
            locale: 'locale',
            upperLocale: 'upperLocale',
            getPlaces: 'places/getPlaces',
            placesOrderByDistance: 'places/getPlacesOrderByDistance',
            getSelectedPlacePanel: 'places/getSelectedPlacePanel',
        }),
        placesAvailables() {
            return reduce(this.places, (result, place) => {
                if (this.zipCodeAnswer && place.zipCodesServed && place.zipCodesServed.length) {
                    result[place.id] = isZipCodeServed({
                        zipCodesServed: place.zipCodesServed,
                        zipCodeAnswer: this.zipCodeAnswer,
                    });
                }
                else {
                    result[place.id] = true;
                }
                return result;
            }, {});
        },
        places() {
            return this.placesOrderByDistance
                ? this.placesOrderByDistance
                : this.getPlaces;
        },
        zipCodeAnswer: {
            get: function() {
                const postalCode = this.$store.getters['places/getPostalCode'];
                const postalCodeIsValid = this.$store.getters['places/getPostalCodeIsValid'];
                this.showPlacesList(postalCodeIsValid);
                return postalCode;
            },
        },
        currentPanel: {
            get: function() {
                return this.getSelectedPlacePanel;
            },
            set: function(value) {
                this.$store.commit('places/SET_SELECTED_PLACE_PANEL', value);
            },
        },
    },
    methods: {
        isContinueButtonDisabled(id) {
            return this.loading || !this.placesAvailables[id];
        },
        ariaLabelPlaceSelected(place) {
            return this.$t('appt.accessibility.selectPlace', { placeName: place['name' + this.upperLocale], address: place.formattedAddress ?? this.$t('appt.accessibility.unknowPlaceAddress') });
        },
        placeNameId(placeId) {
            return `place-name-${placeId}`;
        },
        tooltipId(placeId) {
            return `tooltip-place-${placeId}`;
        },
        formatDistance(val) {
            return val || parseInt(val) === 0 ? ` ~ ${val} km` : '';
        },
        nextStep() {
            this.$nextTick(() => {
                this.$emit('nextStep');
            });
        },
        checkPanelClick(index) {
            if(this.placePanel[index]) {
                this.openPanel(-1);
            }
            else {
                this.onPanelInput(index);
                this.openPanel(index);
            }
        },

        openPanel(index) {
            this.placePanel = [];
            this.panelReadOnly = false;
            this.$nextTick(() => {
                this.setOpenPanelIndex(index);
                this.panelReadOnly = true;
            });
        },

        onPanelInput(index, id) {
            if(id && this.isContinueButtonDisabled(id)) {
                return;
            }

            if(this.$vuetify.breakpoint.mdAndUp) {
                this.setOpenPanelIndex(-1);
            }
            else {
                this.setOpenPanelIndex(index);
            }
            this.currentPanel = index;
            this.$emit('onPanelInput', index);

            if(id) {
                this.nextStep();
                // GA4 GTM event
                this.publicSelectPlaceAnalytics(index);
            }
        },

        setOpenPanelIndex(index) {
            for(let i = 0; i < this.places.length; i++) {
                this.placePanel[i] = i === index;
            }
        },

        /**
         *  Validate postal code from Google and show/hide distance
         */
        async filterPlaces() {
            this.$store.commit('places/SET_POSTAL_CODE_FROM_PORTAL', false);
            try {
                this.loadingPlace = true;
                const postalCodeLatLng = await this.getLatLngFromPostalCode(this.zipCodeAnswer);
                this.zipcodeError = false;

                if (isNil(postalCodeLatLng)) {
                    this.showDistance = false;
                }
                else {
                    this.$store.commit('places/SET_LAT', postalCodeLatLng.lat);
                    this.$store.commit('places/SET_LNG', postalCodeLatLng.lng);
                    this.showDistance = true;

                    const isSelectedPlace = this.$store.getters['places/getIsSelectedPlace'];
                    const selectedPlaceIdFromPortal = this.$store.getters['places/getSelectedPlaceId'];

                    // set panel active if place comes from portal
                    if (selectedPlaceIdFromPortal && isSelectedPlace) {
                        this.$nextTick(() => {
                            let panelIndex = findIndex(this.places, ['id', selectedPlaceIdFromPortal]);
                            this.$store.commit('places/SET_SELECTED_PLACE_ID', '');
                            this.checkPanelClick(panelIndex);
                        });
                    }
                }
            }
            catch (e) {
                this.zipcodeError = true;
                throw e;
            }
            finally {
                this.loadingPlace = false;
            }
        },

        /**
         * Retrieves the lat/lng coords for a postal code from Geocoder for distance calculation
         * @param postalCode
         */
        async getLatLngFromPostalCode(postalCode) {
            const geocodingResult = await this.$store.dispatch('other/geocoder', postalCode.replace(' ', ''));

            return geocodingResult ? { lat: geocodingResult.latitude, lng: geocodingResult.longitude } : null;
        },

        showPlacesList(postalCodeIsValid) {
            if (postalCodeIsValid) {
                const postalCodeFromPortal = this.$store.getters['places/getPostalCodeFromPortal'];
                if (postalCodeFromPortal) {
                    // if code postal come from portal, filter place by distance
                    this.filterPlaces();
                }
                this.displayPlace = true;
            }
        },
        showKm(isDistanceShown) {
            return this.showDistance && isDistanceShown;
        },
        shouldShowAddress(isDistanceShown) {
            return isDistanceShown;
        },
    },
};
</script>

<style lang="scss" scoped>
@import './src/assets/scss/variables';

    .sm-border {
        /*border-top: 1px #ccc solid;*/
        border-bottom: 1px #ccc solid;
    }

.v-expansion-panel-odd-background {
    &.active-header-background {
        background-color: $primary-base !important;
    }

    &:nth-child(even) {
        background-color: #ddd;
        border-radius: 3px;
    }
}
</style>
